import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  History,
  TrendingUp,
  TrendingDown,
  Download,
  Visibility,
  Assessment,
  Payment,
  Schedule,
  Warning,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { LoanApplication, RepaymentSchedule, Transaction } from '../types';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const LoanHistory: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');

  // Mock data - replace with actual API calls
  const [loanHistory, setLoanHistory] = useState<LoanApplication[]>([]);
  const [repaymentHistory, setRepaymentHistory] = useState<RepaymentSchedule[]>([]);
  const [transactionHistory, setTransactionHistory] = useState<Transaction[]>([]);

  useEffect(() => {
    const fetchLoanHistory = async () => {
      try {
        setLoading(true);
        // Mock data - replace with actual API call
        const mockLoanHistory: LoanApplication[] = [
          {
            id: 'L001',
            customerType: 'individual',
            applicantName: 'John Doe',
            email: 'john@example.com',
            phone: '+1234567890',
            nationalId: 'ID001',
            monthlyIncome: 500000,
            requestedAmount: 1000000,
            employmentStatus: 'employed',
            employmentDuration: 5,
            age: 35,
            existingLoans: 1,
            documents: {},
            creditScore: 720,
            status: 'disbursed',
            interestRate: 12.5,
            submissionDate: '2023-03-15T10:00:00Z',
            disbursementDate: '2023-03-20T14:30:00Z',
            loanTerm: 24,
            monthlyPayment: 47000,
          },
          {
            id: 'L002',
            customerType: 'sme',
            applicantName: 'Jane Smith',
            email: 'jane@business.com',
            phone: '+1234567891',
            nationalId: 'ID002',
            monthlyIncome: 800000,
            requestedAmount: 2000000,
            employmentStatus: 'business-owner',
            employmentDuration: 8,
            age: 42,
            existingLoans: 0,
            documents: {},
            businessName: 'Smith Enterprises',
            businessType: 'Retail',
            yearsInBusiness: 8,
            creditScore: 750,
            status: 'disbursed',
            interestRate: 11.0,
            submissionDate: '2023-04-10T09:15:00Z',
            disbursementDate: '2023-04-15T11:20:00Z',
            loanTerm: 36,
            monthlyPayment: 65000,
          },
          {
            id: 'L003',
            customerType: 'individual',
            applicantName: 'Bob Johnson',
            email: 'bob@example.com',
            phone: '+1234567892',
            nationalId: 'ID003',
            monthlyIncome: 400000,
            requestedAmount: 800000,
            employmentStatus: 'employed',
            employmentDuration: 3,
            age: 28,
            existingLoans: 2,
            documents: {},
            creditScore: 680,
            status: 'rejected',
            interestRate: 0,
            submissionDate: '2023-05-20T16:45:00Z',
          },
        ];

        const mockRepaymentHistory: RepaymentSchedule[] = [
          {
            id: 'R001',
            loanId: 'L001',
            installmentNumber: 1,
            dueDate: '2023-04-20',
            amount: 47000,
            principal: 42000,
            interest: 5000,
            status: 'paid',
            paymentDate: '2023-04-19',
            remainingBalance: 958000,
          },
          {
            id: 'R002',
            loanId: 'L001',
            installmentNumber: 2,
            dueDate: '2023-05-20',
            amount: 47000,
            principal: 42000,
            interest: 5000,
            status: 'paid',
            paymentDate: '2023-05-18',
            remainingBalance: 916000,
          },
          {
            id: 'R003',
            loanId: 'L001',
            installmentNumber: 3,
            dueDate: '2023-06-20',
            amount: 47000,
            principal: 42000,
            interest: 5000,
            status: 'overdue',
            remainingBalance: 874000,
          },
        ];

        const mockTransactionHistory: Transaction[] = [
          {
            id: 'T001',
            loanId: 'L001',
            type: 'disbursement',
            amount: 1000000,
            timestamp: '2023-03-20T14:30:00Z',
            description: 'Loan disbursement',
            reference: 'DIS-001',
            status: 'completed',
            netAmount: 1000000,
          },
          {
            id: 'T002',
            loanId: 'L001',
            type: 'repayment',
            amount: 47000,
            timestamp: '2023-04-19T10:00:00Z',
            description: 'Monthly repayment',
            reference: 'REP-001',
            status: 'completed',
            fees: 0,
            netAmount: 47000,
          },
        ];

        setLoanHistory(mockLoanHistory);
        setRepaymentHistory(mockRepaymentHistory);
        setTransactionHistory(mockTransactionHistory);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch loan history');
        setLoading(false);
      }
    };

    fetchLoanHistory();
  }, []);

  const filteredLoans = loanHistory.filter(loan => {
    if (filterStatus !== 'all' && loan.status !== filterStatus) return false;
    if (filterType !== 'all' && loan.customerType !== filterType) return false;
    
    const loanDate = new Date(loan.submissionDate);
    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    return loanDate >= startDate && loanDate <= endDate;
  });

  const disbursedLoans = filteredLoans.filter(loan => loan.status === 'disbursed');
  const rejectedLoans = filteredLoans.filter(loan => loan.status === 'rejected');
  const pendingLoans = filteredLoans.filter(loan => loan.status === 'pending');

  const monthlyData = [
    { month: 'Jan 2023', applications: 45, disbursements: 38, rejections: 7, amount: 85000000 },
    { month: 'Feb 2023', applications: 52, disbursements: 44, rejections: 8, amount: 92000000 },
    { month: 'Mar 2023', applications: 48, disbursements: 41, rejections: 7, amount: 88000000 },
    { month: 'Apr 2023', applications: 55, disbursements: 47, rejections: 8, amount: 95000000 },
    { month: 'May 2023', applications: 50, disbursements: 43, rejections: 7, amount: 90000000 },
    { month: 'Jun 2023', applications: 58, disbursements: 50, rejections: 8, amount: 98000000 },
  ];

  const exportToXLS = (data: any[], fileName: string) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const exportToPDF = (headers: string[][], body: any[][], fileName: string) => {
    const doc = new jsPDF();
    autoTable(doc, { head: headers, body });
    doc.save(`${fileName}.pdf`);
  };

  const handleExportLoans = (format: 'xls' | 'pdf') => {
    const data = filteredLoans.map(loan => ({
      'Loan ID': loan.id,
      'Applicant Name': loan.applicantName,
      'Customer Type': loan.customerType,
      'Requested Amount': loan.requestedAmount,
      'Status': loan.status,
      'Credit Score': loan.creditScore || 'N/A',
      'Submission Date': new Date(loan.submissionDate).toLocaleDateString(),
      'Interest Rate': loan.interestRate ? `${loan.interestRate}%` : 'N/A',
    }));

    if (format === 'xls') {
      exportToXLS(data, 'loan-history');
    } else {
      const head = [['Loan ID', 'Applicant Name', 'Customer Type', 'Requested Amount', 'Status', 'Credit Score', 'Submission Date', 'Interest Rate']];
      const body = data.map(d => Object.values(d));
      exportToPDF(head, body, 'loan-history');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        One-Year Loan History
      </Typography>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Applications
                </Typography>
                <Typography variant="h5" component="div">
                  {filteredLoans.length}
                </Typography>
              </Box>
              <History color="primary" />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Disbursed Loans
                </Typography>
                <Typography variant="h5" component="div" color="success.main">
                  {disbursedLoans.length}
                </Typography>
                <Typography variant="body2" color="success.main">
                  {filteredLoans.length > 0 ? ((disbursedLoans.length / filteredLoans.length) * 100).toFixed(1) : 0}%
                </Typography>
              </Box>
              <CheckCircle color="success" />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Rejected Loans
                </Typography>
                <Typography variant="h5" component="div" color="error.main">
                  {rejectedLoans.length}
                </Typography>
                <Typography variant="body2" color="error.main">
                  {filteredLoans.length > 0 ? ((rejectedLoans.length / filteredLoans.length) * 100).toFixed(1) : 0}%
                </Typography>
              </Box>
              <Error color="error" />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Disbursed
                </Typography>
                <Typography variant="h5" component="div" color="primary.main">
                  {(disbursedLoans.reduce((sum, loan) => sum + (loan.requestedAmount || 0), 0) / 1000000).toFixed(1)}M ZiG
                </Typography>
              </Box>
              <TrendingUp color="primary" />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Start Date"
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="disbursed">Disbursed</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Customer Type</InputLabel>
            <Select
              value={filterType}
              label="Customer Type"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="individual">Individual</MenuItem>
              <MenuItem value="sme">SME</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Overview" />
          <Tab label="Loan Details" />
          <Tab label="Repayment History" />
          <Tab label="Transaction History" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          {/* Charts */}
          <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
            <Paper sx={{ p: 2, flex: '1 1 400px', minWidth: '400px' }}>
              <Typography variant="h6" gutterBottom>
                Monthly Trends
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="applications" stroke="#8884d8" name="Applications" />
                  <Line type="monotone" dataKey="disbursements" stroke="#82ca9d" name="Disbursements" />
                  <Line type="monotone" dataKey="rejections" stroke="#ffc658" name="Rejections" />
                </LineChart>
              </ResponsiveContainer>
            </Paper>

            <Paper sx={{ p: 2, flex: '1 1 400px', minWidth: '400px' }}>
              <Typography variant="h6" gutterBottom>
                Monthly Disbursements
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="amount" fill="#8884d8" name="Amount (ZiG)" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Box>
        </Box>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 2, mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Loan Applications ({filteredLoans.length})
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={() => handleExportLoans('xls')}
              >
                Export XLS
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={() => handleExportLoans('pdf')}
              >
                Export PDF
              </Button>
            </Box>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Loan ID</TableCell>
                  <TableCell>Applicant</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Credit Score</TableCell>
                  <TableCell>Submission Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLoans.map((loan) => (
                  <TableRow key={loan.id}>
                    <TableCell>{loan.id}</TableCell>
                    <TableCell>{loan.applicantName}</TableCell>
                    <TableCell>
                      <Chip
                        label={loan.customerType}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{loan.requestedAmount?.toLocaleString('en-ZW')} ZiG</TableCell>
                    <TableCell>
                      <Chip
                        label={loan.status}
                        color={
                          loan.status === 'disbursed' ? 'success' :
                          loan.status === 'rejected' ? 'error' :
                          loan.status === 'approved' ? 'primary' : 'warning'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{loan.creditScore || 'N/A'}</TableCell>
                    <TableCell>{new Date(loan.submissionDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {activeTab === 2 && (
        <Paper sx={{ p: 2, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Repayment History
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Loan ID</TableCell>
                  <TableCell>Installment</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Principal</TableCell>
                  <TableCell>Interest</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Remaining Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {repaymentHistory.map((repayment) => (
                  <TableRow key={repayment.id}>
                    <TableCell>{repayment.loanId}</TableCell>
                    <TableCell>{repayment.installmentNumber}</TableCell>
                    <TableCell>{new Date(repayment.dueDate).toLocaleDateString()}</TableCell>
                    <TableCell>{repayment.amount.toLocaleString('en-ZW')} ZiG</TableCell>
                    <TableCell>{repayment.principal.toLocaleString('en-ZW')} ZiG</TableCell>
                    <TableCell>{repayment.interest.toLocaleString('en-ZW')} ZiG</TableCell>
                    <TableCell>
                      <Chip
                        label={repayment.status}
                        color={
                          repayment.status === 'paid' ? 'success' :
                          repayment.status === 'overdue' ? 'warning' :
                          repayment.status === 'defaulted' ? 'error' : 'default'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{repayment.remainingBalance.toLocaleString('en-ZW')} ZiG</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {activeTab === 3 && (
        <Paper sx={{ p: 2, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Transaction History
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>Loan ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactionHistory.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>{transaction.id}</TableCell>
                    <TableCell>{transaction.loanId}</TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.type}
                        color={
                          transaction.type === 'disbursement' ? 'primary' :
                          transaction.type === 'repayment' ? 'success' :
                          transaction.type === 'fee' ? 'warning' : 'info'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{transaction.amount.toLocaleString('en-ZW')} ZiG</TableCell>
                    <TableCell>{new Date(transaction.timestamp).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.status}
                        color={transaction.status === 'completed' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{transaction.reference}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default LoanHistory;
